import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import React__default, { useRef, useCallback, useState, useEffect } from 'react';
import './DatepickerCalendar.scss.js';
import Tooltip from '../../Tooltip/Tooltip.js';
import { stringToDate, getDaysForMonth, isCurrentDay, months, isCurrentMonth, compareMonths, weekDays, formatDate } from '../../../../utils/helpersDatePicker.js';
import ArrowsChevronLeft from '../../../../assets/icons/resultDir/ArrowsChevronLeft.js';

var DatepickerCalendar = function DatepickerCalendar(_ref) {
  var value = _ref.value,
      setInputValue = _ref.setInputValue,
      toggleCalendar = _ref.toggleCalendar,
      minDate = _ref.minDate,
      maxDate = _ref.maxDate,
      range = _ref.range,
      _ref$locale = _ref.locale,
      locale = _ref$locale === void 0 ? 'ru' : _ref$locale,
      _ref$format = _ref.format,
      format = _ref$format === void 0 ? 'dd.mm.yyyy' : _ref$format,
      _ref$separator = _ref.separator,
      separator = _ref$separator === void 0 ? '-' : _ref$separator,
      _ref$disableWeekDays = _ref.disableWeekDays,
      disableWeekDays = _ref$disableWeekDays === void 0 ? [] : _ref$disableWeekDays,
      _ref$tooltipBackgroun = _ref.tooltipBackground,
      tooltipBackground = _ref$tooltipBackgroun === void 0 ? 'white' : _ref$tooltipBackgroun;
  var contentRef = useRef(null); // -------------------------------------------------------------------------------------------------------------------

  var setCurrent = useCallback(function () {
    if (value && !range) {
      return stringToDate(value, format);
    }

    return new Date();
  }, [value, range]);

  var setRange = function setRange() {
    var _a, _b;

    if (value && range) {
      var values = value.split(' - ');
      var from = ((_a = values[0]) === null || _a === void 0 ? void 0 : _a.includes('_')) ? undefined : values[0];
      var to = ((_b = values[1]) === null || _b === void 0 ? void 0 : _b.includes('_')) ? undefined : values[1];
      return [from ? stringToDate(from, format) : undefined, to ? stringToDate(to, format) : undefined];
    }

    return [undefined, undefined];
  };

  var _useState = useState(setCurrent()),
      _useState2 = _slicedToArray(_useState, 2),
      currentDate = _useState2[0],
      setCurrentDate = _useState2[1];

  var _useState3 = useState([undefined, undefined]),
      _useState4 = _slicedToArray(_useState3, 2),
      rangeDates = _useState4[0],
      setRangeDates = _useState4[1];

  useEffect(function () {
    if (range) {
      setRangeDates(setRange());
    } else {
      setCurrentDate(setCurrent());
    }
  }, [value, range]); // -------------------------------------------------------------------------------------------------------------------

  var onDateChange = function onDateChange(date) {
    var _a, _b, _c;

    if (range) {
      var dates = _toConsumableArray(rangeDates);

      if (dates[0] !== undefined && dates[1] !== undefined) {
        dates = [undefined, undefined];
      }

      if (dates[0] === undefined) {
        dates[0] = date;
        setInputValue(formatDate(date.getTime(), format).date + " - __".concat(separator, "__").concat(separator, "____"));
      } else {
        dates[1] = date;

        if (dates[1].getTime() < ((_a = dates[0]) === null || _a === void 0 ? void 0 : _a.getTime())) {
          dates = [dates[1], dates[0]];
        }

        var newValue = "".concat(formatDate((_b = dates[0]) === null || _b === void 0 ? void 0 : _b.getTime(), format).date, " - ").concat(formatDate((_c = dates[1]) === null || _c === void 0 ? void 0 : _c.getTime(), format).date);
        setInputValue(newValue);
        toggleCalendar(false);
      }
    } else {
      setInputValue(formatDate(date.getTime(), format).date);
      setCurrentDate(date);
      toggleCalendar(false);
    }

    setPeriodType('day');
  }; // -------------------------------------------------------------------------------------------------------------------


  var _useState5 = useState(getDaysForMonth(currentDate)),
      _useState6 = _slicedToArray(_useState5, 2),
      activePeriod = _useState6[0],
      setActivePeriod = _useState6[1];

  useEffect(function () {
    if (!range) {
      setActivePeriod(getDaysForMonth(currentDate));

      if (minDate && currentDate.getTime() < minDate.getTime()) {
        setActivePeriod(getDaysForMonth(minDate));
      }

      if (maxDate && currentDate.getTime() > maxDate.getTime()) {
        setActivePeriod(getDaysForMonth(maxDate));
      }
    } else {
      if (minDate && rangeDates[0] && rangeDates[0].getTime() < minDate.getTime()) {
        setActivePeriod(getDaysForMonth(minDate));
      }

      if (maxDate && rangeDates[1] && rangeDates[1].getTime() > maxDate.getTime()) {
        setActivePeriod(getDaysForMonth(maxDate));
      }
    }
  }, [currentDate, range, minDate, maxDate]);
  useEffect(function () {
    if (range) {
      if (rangeDates[1] !== undefined) {
        setActivePeriod(getDaysForMonth(rangeDates[1]));
      } else if (rangeDates[0] !== undefined) {
        setActivePeriod(getDaysForMonth(rangeDates[0]));
      } else {
        setActivePeriod(getDaysForMonth(new Date()));
      }
    }
  }, [rangeDates, range]); // -------------------------------------------------------------------------------------------------------------------

  var _onBlur = function onBlur(lastButton) {
    if (lastButton) {
      toggleCalendar(false);
    }
  }; // -------------------------------------------------------------------------------------------------------------------


  var onDayClick = function onDayClick(date) {
    onDateChange(date);
  };

  var daysJSX = activePeriod.days.map(function (_ref2, i, array) {
    var period = _ref2.period,
        date = _ref2.date;

    var _a, _b;

    var periodClass = "rf-datepicker__calendar-day--".concat(period);
    var isToday = new Date().toDateString() === date.toDateString();
    var rangeDayCondition = rangeDates[0] && isCurrentDay(date, rangeDates[0]) || rangeDates[1] && isCurrentDay(date, rangeDates[1]);
    var activeCondition = range ? rangeDayCondition : isCurrentDay(date, currentDate);
    var currentDayClass = activeCondition ? 'rf-datepicker__calendar-date--active' : isToday ? 'rf-datepicker__today-day' : '';
    var fromDateClass = rangeDates[0] && ((_a = rangeDates[0]) === null || _a === void 0 ? void 0 : _a.getTime()) === date.getTime() ? 'rf-datepicker__calendar-date--from' : '';
    var toDateClass = rangeDates[1] && ((_b = rangeDates[1]) === null || _b === void 0 ? void 0 : _b.getTime()) === date.getTime() ? 'rf-datepicker__calendar-date--to' : '';
    var inRangeClass = range && rangeDates[0] && rangeDates[1] && date.getTime() >= rangeDates[0].getTime() && date.getTime() <= rangeDates[1].getTime() ? 'rf-datepicker__calendar-tile--range rf-datepicker__calendar-date--range  ' : '';
    var d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    var disabledWeekDay = disableWeekDays.includes(d.getDay());
    var disabledMin = minDate && minDate.getTime() > d.getTime();
    var disabledMax = maxDate && maxDate.getTime() < d.getTime();
    var disabledClass = disabledWeekDay || disabledMin || disabledMax ? 'rf-datepicker__calendar-date--disabled' : '';
    return /*#__PURE__*/React__default.createElement("button", {
      disabled: disabledWeekDay || disabledMin || disabledMax,
      key: date.getTime(),
      type: "button",
      className: "rf-datepicker__calendar-tile rf-datepicker__calendar-date rf-datepicker__calendar-day\n          ".concat(periodClass, "\n          ").concat(currentDayClass, "\n          ").concat(disabledClass, " \n          ").concat(fromDateClass, "\n          ").concat(toDateClass, " \n          ").concat(inRangeClass, "\n        "),
      onClick: function onClick() {
        return onDayClick(date);
      },
      onBlur: function onBlur() {
        return _onBlur(i === array.length - 1);
      },
      "data-v-6e52f9c7": ""
    }, date.getDate());
  }); // -------------------------------------------------------------------------------------------------------------------

  var onMonthClick = function onMonthClick(e, monthIndex) {
    e.stopPropagation();
    setActivePeriod(getDaysForMonth(new Date(activePeriod.year, monthIndex)));
    setTimeout(function () {
      setPeriodType('day');
    });
  };

  var monthsJSX = months[locale].map(function (m, i, array) {
    var d = new Date(activePeriod.year, i);
    var rangeMonthCondition = rangeDates[0] && isCurrentMonth(d, rangeDates[0]) || rangeDates[1] && isCurrentMonth(d, rangeDates[1]);
    var activeCondition = range ? rangeMonthCondition : isCurrentMonth(d, currentDate);
    var currentMonthClass = activeCondition ? 'rf-datepicker__calendar-date--active' : '';
    var fromMonthCondition = rangeDates[0] && isCurrentMonth(d, rangeDates[0]);
    var fromMonthClass = fromMonthCondition ? 'rf-datepicker__calendar-month-wrapper--from' : '';
    var toMonthClass = rangeDates[1] && isCurrentMonth(d, rangeDates[1]) ? 'rf-datepicker__calendar-month-wrapper--to' : '';
    var inRangeCondition = range && rangeDates[0] && rangeDates[1] && compareMonths(d, rangeDates[0]) >= 0 && compareMonths(d, rangeDates[1]) <= 0;
    var inRangeClass = inRangeCondition ? 'rf-datepicker__calendar-tile--range rf-datepicker__calendar-month-wrapper--range' : '';
    var monthMs = 1000 * 3600 * 24 * 31;
    var disabledMin = minDate && minDate.getTime() - monthMs > d.getTime();
    var disabledMax = maxDate && maxDate.getTime() < d.getTime();
    var disabledClass = disabledMin || disabledMax ? 'rf-datepicker__calendar-date--disabled' : '';
    return /*#__PURE__*/React__default.createElement("div", {
      key: m,
      className: "rf-datepicker__calendar-month-wrapper ".concat(inRangeClass, " ").concat(fromMonthClass, " ").concat(toMonthClass),
      "data-v-6e52f9c7": ""
    }, /*#__PURE__*/React__default.createElement("button", {
      type: "button",
      className: "rf-datepicker__calendar-tile rf-datepicker__calendar-date rf-datepicker__calendar-month\n        ".concat(currentMonthClass, " ").concat(disabledClass),
      onClick: function onClick(e) {
        return onMonthClick(e, i);
      },
      onBlur: function onBlur() {
        return _onBlur(i === array.length - 1);
      },
      "data-v-6e52f9c7": ""
    }, m));
  }); // -------------------------------------------------------------------------------------------------------------------

  var stringYear = activePeriod.year.toString();

  var _useState7 = useState(activePeriod.year - +stringYear[stringYear.length - 1]),
      _useState8 = _slicedToArray(_useState7, 2),
      decadeStart = _useState8[0],
      setDecadeStart = _useState8[1];

  useEffect(function () {
    setDecadeStart(activePeriod.year - +stringYear[stringYear.length - 1]);
  }, [activePeriod]);
  var years = [];

  for (var i = 0; i < 10; i++) {
    years.push(decadeStart + i);
  }

  var onYearClick = function onYearClick(e, year) {
    e.stopPropagation();
    setActivePeriod(getDaysForMonth(new Date(year, activePeriod.month)));
    setTimeout(function () {
      setPeriodType('month');
    });
  };

  var yearsJSX = years.map(function (y, i, array) {
    var _a, _b, _c, _d, _e;

    var rangeMonthCondition = rangeDates[0] && y === rangeDates[0].getFullYear() || rangeDates[1] && y === ((_a = rangeDates[1]) === null || _a === void 0 ? void 0 : _a.getFullYear());
    var activeCondition = range ? rangeMonthCondition : activePeriod.year === y;
    var currentMonthClass = activeCondition ? 'rf-datepicker__calendar-date--active' : '';
    var fromYearClass = rangeDates[0] && ((_b = rangeDates[0]) === null || _b === void 0 ? void 0 : _b.getFullYear()) === y ? 'rf-datepicker__calendar-year--from' : '';
    var toYearClass = rangeDates[1] && ((_c = rangeDates[1]) === null || _c === void 0 ? void 0 : _c.getFullYear()) === y ? 'rf-datepicker__calendar-year--to' : '';
    var inRangeClass = range && rangeDates[0] && rangeDates[1] && y >= ((_d = rangeDates[0]) === null || _d === void 0 ? void 0 : _d.getFullYear()) && y <= ((_e = rangeDates[1]) === null || _e === void 0 ? void 0 : _e.getFullYear()) ? 'rf-datepicker__calendar-tile--range rf-datepicker__calendar-year--range' : '';
    var disabledMin = minDate && minDate.getFullYear() > y;
    var disabledMax = maxDate && maxDate.getFullYear() < y;
    var disabledClass = disabledMin || disabledMax ? 'rf-datepicker__calendar-date--disabled' : '';
    return /*#__PURE__*/React__default.createElement("div", {
      key: y,
      className: "rf-datepicker__calendar-year-wrapper ".concat(inRangeClass, " ").concat(fromYearClass, " ").concat(toYearClass),
      "data-v-6e52f9c7": ""
    }, /*#__PURE__*/React__default.createElement("button", {
      type: "button",
      className: "rf-datepicker__calendar-tile rf-datepicker__calendar-date rf-datepicker__calendar-year\n        ".concat(currentMonthClass, " ").concat(disabledClass),
      onClick: function onClick(e) {
        return onYearClick(e, y);
      },
      onBlur: function onBlur() {
        return _onBlur(i === array.length - 1);
      },
      "data-v-6e52f9c7": ""
    }, y));
  }); // -------------------------------------------------------------------------------------------------------------------

  var onPeriodChange = function onPeriodChange(n) {
    if (periodType === 'day') {
      var nextMonth = activePeriod.month + n;
      var nextYear = activePeriod.year;

      if (nextMonth > 11) {
        nextMonth = 0;
        nextYear++;
      }

      if (nextMonth < 0) {
        nextMonth = 11;
        nextYear--;
      }

      setActivePeriod(getDaysForMonth(new Date(nextYear, nextMonth)));
    }

    if (periodType === 'month') {
      setActivePeriod(getDaysForMonth(new Date(activePeriod.year + n, activePeriod.month)));
    }

    if (periodType === 'year') {
      setDecadeStart(function (decade) {
        return decade + n * 10;
      });
    }
  }; // -------------------------------------------------------------------------------------------------------------------


  var _useState9 = useState('day'),
      _useState10 = _slicedToArray(_useState9, 2),
      periodType = _useState10[0],
      setPeriodType = _useState10[1];

  var periodTypeLabel = {
    day: /*#__PURE__*/React__default.createElement(React__default.Fragment, null, " ", months[locale][activePeriod.month], " "),
    month: /*#__PURE__*/React__default.createElement(React__default.Fragment, null, " ", activePeriod.year, " "),
    year: /*#__PURE__*/React__default.createElement(React__default.Fragment, null, " ", decadeStart, " - ", decadeStart + 9, " ")
  };

  var onPeriodTypeChange = function onPeriodTypeChange(periodType) {
    return function () {
      setPeriodType(periodType);
    };
  }; // -------------------------------------------------------------------------------------------------------------------


  var prevYearDisabled = !!minDate && minDate.getFullYear() > activePeriod.year - 1;
  var nextYearDisabled = !!maxDate && maxDate.getFullYear() < activePeriod.year + 1;
  var arrowsDisabled = {
    prevArrowDisabled: {
      day: prevYearDisabled && !!minDate && minDate.getMonth() > activePeriod.month - 1,
      month: prevYearDisabled,
      year: !!minDate && minDate.getFullYear() > decadeStart
    },
    nextArrowDisabled: {
      day: nextYearDisabled && !!maxDate && maxDate.getMonth() < activePeriod.month + 1,
      month: nextYearDisabled,
      year: !!maxDate && maxDate.getFullYear() < decadeStart + 10
    }
  };
  var prevArrowDisabled = arrowsDisabled.prevArrowDisabled[periodType];
  var nextArrowDisabled = arrowsDisabled.nextArrowDisabled[periodType];
  var d = new Date();
  var today = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  !!minDate && minDate.getTime() > today.getTime();
  !!maxDate && maxDate.getTime() < today.getTime(); // -------------------------------------------------------------------------------------------------------------------

  var getButtonWithTooltip = function getButtonWithTooltip(period, tipText) {
    return /*#__PURE__*/React__default.createElement(Tooltip, {
      background: tooltipBackground,
      className: "rf-datepicker-calendar__tooltip",
      "data-v-6e52f9c7": ""
    }, /*#__PURE__*/React__default.createElement("button", {
      type: "button",
      className: "rf-datepicker-calendar__button rf-datepicker-calendar__label-button",
      onClick: onPeriodTypeChange(period),
      "data-v-6e52f9c7": ""
    }, /*#__PURE__*/React__default.createElement("span", {
      className: "rf-datepicker__calendar-label",
      "data-v-6e52f9c7": ""
    }, periodTypeLabel[period === 'month' ? 'day' : 'month'])), /*#__PURE__*/React__default.createElement("div", {
      "data-v-6e52f9c7": ""
    }, tipText));
  };

  return /*#__PURE__*/React__default.createElement("div", {
    className: "rf-datepicker__calendar",
    ref: contentRef,
    "data-v-6e52f9c7": ""
  }, /*#__PURE__*/React__default.createElement("header", {
    className: "rf-datepicker__calendar-header",
    "data-v-6e52f9c7": ""
  }, /*#__PURE__*/React__default.createElement("div", {
    className: "rf-datepicker-calendar__control",
    "data-v-6e52f9c7": ""
  }, /*#__PURE__*/React__default.createElement("button", {
    type: "button",
    className: "rf-datepicker-calendar__button rf-datepicker-calendar__button--arrow rf-datepicker-calendar__button-prev",
    disabled: prevArrowDisabled,
    onClick: function onClick() {
      return onPeriodChange(-1);
    },
    "data-v-6e52f9c7": ""
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "rf-datepicker__calendar-left",
    "data-v-6e52f9c7": ""
  }, /*#__PURE__*/React__default.createElement(ArrowsChevronLeft, {
    "data-v-6e52f9c7": ""
  }))), /*#__PURE__*/React__default.createElement("div", {
    className: "rf-datepicker-calendar__control__button-section",
    "data-v-6e52f9c7": ""
  }, getButtonWithTooltip('month', 'Выбор месяца'), getButtonWithTooltip('year', 'Выбор года')), /*#__PURE__*/React__default.createElement("button", {
    type: "button",
    className: "rf-datepicker-calendar__button rf-datepicker-calendar__button--arrow rf-datepicker-calendar__button-next",
    disabled: nextArrowDisabled,
    onClick: function onClick() {
      return onPeriodChange(1);
    },
    "data-v-6e52f9c7": ""
  }, /*#__PURE__*/React__default.createElement("span", {
    className: "rf-datepicker__calendar-right",
    "data-v-6e52f9c7": ""
  }, /*#__PURE__*/React__default.createElement(ArrowsChevronLeft, {
    "data-v-6e52f9c7": ""
  }))))), /*#__PURE__*/React__default.createElement("div", {
    "data-v-6e52f9c7": ""
  }, periodType === 'day' && /*#__PURE__*/React__default.createElement("div", {
    className: "rf-datepicker__calendar-week",
    "data-v-6e52f9c7": ""
  }, weekDays[locale].map(function (d) {
    return /*#__PURE__*/React__default.createElement("div", {
      className: "rf-datepicker__calendar-tile rf-datepicker__calendar-week-day",
      key: d,
      "data-v-6e52f9c7": ""
    }, d);
  })), /*#__PURE__*/React__default.createElement("div", {
    className: "rf-datepicker__calendar-periods",
    "data-v-6e52f9c7": ""
  }, periodType === 'day' && daysJSX, /*#__PURE__*/React__default.createElement("div", {
    className: "rf-datepicker__calendar-periods-month",
    "data-v-6e52f9c7": ""
  }, periodType === 'month' && monthsJSX), /*#__PURE__*/React__default.createElement("div", {
    className: "rf-datepicker__calendar-periods-year",
    "data-v-6e52f9c7": ""
  }, periodType === 'year' && yearsJSX))));
};

export { DatepickerCalendar as default };
